/**
 * Announcement Components
 * Shows DJ/Admin broadcast messages to everyone
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const AnnouncementBanner = ({ announcement, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (announcement) {
      setIsVisible(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss?.(), 500);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [announcement, onDismiss]);

  if (!announcement) return null;

  const getRoleStyle = (role) => {
    switch (role) {
      case 'admin':
      case 'owner':
        return 'from-red-600 to-orange-600 border-red-500/50';
      case 'dj':
        return 'from-pink-600 to-purple-600 border-pink-500/50';
      default:
        return 'from-purple-600 to-blue-600 border-purple-500/50';
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 pointer-events-none"
        >
          <div className={`bg-gradient-to-r ${getRoleStyle(announcement.role)} p-5 rounded-xl border-2 shadow-2xl backdrop-blur-md pointer-events-auto`}
               style={{ boxShadow: '0 0 40px rgba(255,100,50,0.5)' }}>
            <div className="flex items-start gap-3">
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="p-2 bg-white/20 rounded-lg"
              >
                <Megaphone className="w-6 h-6 text-white" />
              </motion.div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white font-bold text-lg">{announcement.senderName}</span>
                  <span className="text-white/80 text-xs uppercase px-2 py-1 bg-white/20 rounded font-bold">
                    {announcement.role}
                  </span>
                </div>
                <p className="text-white text-base font-medium">{announcement.message}</p>
              </div>
              <button
                onClick={() => {
                  setIsVisible(false);
                  setTimeout(() => onDismiss?.(), 300);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            {/* Progress bar showing time remaining */}
            <motion.div 
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: 5, ease: 'linear' }}
              className="mt-3 h-1 bg-white/40 rounded-full origin-left"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AnnouncementModal = ({ isOpen, onClose, onSend }) => {
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl border border-purple-500/30 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg">
            <Megaphone className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Send Announcement</h2>
        </div>

        <p className="text-white/60 text-sm mb-4">
          Your message will be broadcast to all players in the club.
        </p>

        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your announcement..."
          maxLength={200}
          className="bg-black/50 border-white/20 text-white mb-4"
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!message.trim()}
            className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Broadcast
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export { AnnouncementBanner, AnnouncementModal };
